import {useCallback, useState} from 'react';
import NextLink from 'next/link';

import {useUpdateActionPlan} from '#apollo/schema';
import {Button, Icon, Link, Text} from '#component/base';
import {ModalRoot} from '#component/base/Modal';
import {ActionPlanActivatedModal} from '#component/modal/ActionPlanActivatedModal';
import {ActionPlanPage} from '#component/page/ActionPlan/ActionPlan.types';
import {actionPlanPageQuery} from '#component/page/ActionPlan/useActionPlan';
import {RecommendationInput} from '#component/page/EditActionPlan/EditActionPlan.types';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {useViewportSize} from '#hook';
import {sprinkles} from '#style/sprinkles';

interface ButtonsProps {
  actionPlan: ActionPlanPage.ActionPlan;
  recommendationInput: RecommendationInput[];
  touched?: boolean;
  hidden?: boolean;
}
export function Buttons({actionPlan, recommendationInput, touched, hidden}: ButtonsProps) {
  const {isDesktop} = useViewportSize();
  const {track} = useAnalytics();
  const [confirmationModalOpen, setConfimationModalOpen] = useState(false);
  const [updateActionPlan, {loading}] = useUpdateActionPlan({
    refetchQueries: [{query: actionPlanPageQuery}],
  });

  const trackActionPlanChanges = useCallback(
    (ap: ActionPlanPage.ActionPlan, newAdjustments: RecommendationInput[]) => {
      const currentAdjmnts = ap.active
        ? ap.adjustments.filter(a => a.selected).map(a => a.healthFactorId || '')
        : [];
      const newAdjmnts = newAdjustments.filter(a => a.selected).map(a => a.healthFactorId || '');
      const added = newAdjmnts.filter(a => !currentAdjmnts.includes(a));
      const removed = currentAdjmnts.filter(a => !newAdjmnts.includes(a));
      const event: UserActionEvent = {
        evtName: EventType.UserAction,
        actionName: ap.active ? 'AP Edited' : 'AP Customized + Activated',
        data: {
          added,
          removed,
        },
      };
      track<UserActionEvent>(event);
    },
    [track],
  );

  const handleUpdateActionPlanClick = useCallback(async () => {
    const {data} = await updateActionPlan({
      variables: {
        input: {
          id: actionPlan.id,
          active: true,
          adjustments: recommendationInput.map(i => ({
            id: i.id,
            selected: i.selected,
          })),
        },
      },
    });
    if (data) {
      setConfimationModalOpen(true);
      trackActionPlanChanges(actionPlan, recommendationInput);
    }
  }, [actionPlan, recommendationInput, updateActionPlan, trackActionPlanChanges]);

  return (
    <div
      className={sprinkles({
        flex: 'flex',
        dir: ['col', 'col', 'row'],
        gap: 8,
        w: 'full',
        mT: 8,
        pX: [4, 4, 0],
        display: hidden ? 'none' : 'flex',
      })}>
      <Button
        className={sprinkles({shrink: 0, w: ['full', 'full', 'maxContent']})}
        disabled={
          (actionPlan.active && !touched) || recommendationInput.filter(s => s.selected).length < 1
        }
        loading={loading}
        onClick={handleUpdateActionPlanClick}>
        {actionPlan.active ? 'Update Plan' : 'Activate Plan'}
      </Button>

      {isDesktop ? (
        <NextLink href="/actionplan" passHref>
          <Button
            as="a"
            className={sprinkles({shrink: 0, w: ['full', 'full', 'maxContent']})}
            variant="secondary">
            Exit Without Saving
          </Button>
        </NextLink>
      ) : (
        <NextLink href="/actionplan" passHref>
          <div
            className={sprinkles({
              flex: 'flex',
              content: 'center',
              w: 'full',
              mT: 8,
            })}>
            <Link
              className={sprinkles({
                display: 'flex',
                items: 'center',
                w: 'maxContent',
              })}>
              <Icon
                name="Trash"
                className={sprinkles({
                  mR: 8,
                })}
              />
              <Text variant="body">Exit Without Saving</Text>
            </Link>
          </div>
        </NextLink>
      )}
      <ModalRoot open={confirmationModalOpen}>
        <ActionPlanActivatedModal />
      </ModalRoot>
    </div>
  );
}
